import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Link,
  Drawer,
  IconButton,
  CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { getCitationData } from '../../api';
import { showSnackbar } from '../../store/snackBarSlice';

interface Reference {
  node_id: string;
  file_id: number;
  page_label: number;
  score: number;
  content: string;
  file_name?: string;
}

interface PriceReferencesProps {
  references: Reference[];
  title: string;
}

const PriceReferences: React.FC<PriceReferencesProps> = ({ references, title }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isCitationOpen, setIsCitationOpen] = useState(false);
  const [selectedReference, setSelectedReference] = useState<Reference | null>(null);
  const [loading, setLoading] = useState(false);
  const [citationUrl, setCitationUrl] = useState<string | null>(null);

  const handleViewReference = async (reference: Reference) => {
    setSelectedReference(reference);
    setIsCitationOpen(true);
    setLoading(true);
    setCitationUrl(null);
    
    try {
      const response = await getCitationData(
        reference.file_id.toString(),
        (reference.page_label + 1).toString()
      );
      
      if (response && response.citation_url && response.citation_url.length > 0) {
        setCitationUrl(response.citation_url);
      } else {
        console.error('No citation URL found in response');
        dispatch(
          showSnackbar({
            message: 'Failed to load reference document',
            severity: 'error',
          })
        );
      }
    } catch (error) {
      console.error('Error fetching citation URL:', error);
      dispatch(
        showSnackbar({
          message: 'Error loading reference document',
          severity: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };
  
  if (!references || !Array.isArray(references) || references.length === 0) {
    return (
      <Box sx={{ mb: 2 }}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`references-content-${title}`}
            id={`references-header-${title}`}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              References
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ color: '#666666' }}>
              No references available for this price attribute.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`references-content-${title}`}
            id={`references-header-${title}`}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              References
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {references.map((ref: Reference, index: number) => (
                <Link
                  key={ref.node_id}
                  component="button"
                  variant="body2"
                  onClick={() => handleViewReference(ref)}
                  sx={{ display: 'block', mb: 0.5, textAlign: 'left' }}
                >
                  {ref.file_name ? `${ref.file_name} - P ${ref.page_label + 1}` : `Reference ${index + 1} - P ${ref.page_label + 1}`}
                </Link>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Citation Panel */}
      <Drawer
        anchor="right"
        open={isCitationOpen}
        onClose={() => setIsCitationOpen(false)}
        variant="temporary"
        sx={{
          '& .MuiDrawer-paper': {
            width: '50vw',
            boxSizing: 'border-box',
            top: '64px',
            height: 'calc(100% - 64px)',
          },
        }}
      >
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: theme.palette.background.paper,
        }}>
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}>
            <Box>
              <Typography variant="h6">Reference</Typography>
              {selectedReference && (
                <Typography variant="body2" color="text.secondary">
                  {selectedReference.file_name || `File ID: ${selectedReference.file_id}`} - Page {selectedReference.page_label + 1}
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setIsCitationOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ 
            flex: 1,
            p: 2,
            overflowY: 'auto',
          }}>
            {loading ? (
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}>
                <CircularProgress />
              </Box>
            ) : citationUrl ? (
              <Box sx={{ height: '100%' }}>
                <iframe
                  src={citationUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title={`${selectedReference?.file_name || `File ${selectedReference?.file_id}`} - Page ${selectedReference?.page_label !== undefined ? selectedReference.page_label + 1 : ''}`}
                />
              </Box>
            ) : (
              <Typography color="error">
                Unable to load the reference document. Please try again later.
              </Typography>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default PriceReferences;